// src/components/reports/SpecialityBySurgeonStacksEChart.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	Typography,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import globalAxios from "../../../globalAxios.js";
import { SERVER_URL } from "../../../config.js";

import { useAppSelector } from "../../../store/hooks.ts";
import {
	selectReportStartDate,
	selectReportEndDate,
} from "../../../store/slices/reportsSlice.ts";

// ---------------- Helpers ----------------
const UNCATEGORISED = "Uncategorised";
const normaliseSpec = (s: any) => (s ? String(s) : UNCATEGORISED);

// Convert ISO (with time) → YYYY-MM-DD
const toYMD = (iso?: string | null) =>
	iso ? String(iso).slice(0, 10) : undefined;
// Make initials from a surgeon’s full name, e.g. “Alice Smith” → “AS”
const makeAbbr = (fullName: string) =>
	fullName
		.split(/\s+/)
		.filter(Boolean)
		.map((p) => p[0]?.toUpperCase() ?? "")
		.join("");

type Row = Record<string, number | string>; // { speciality: string, "Alice Smith": number, ..., total?: number }

// ---------------- Component ----------------
const SpecialityChart: React.FC = () => {
	const chartRef = useRef<ReactECharts>(null);
	const hostRef = useRef<HTMLDivElement>(null); // container to observe

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [rows, setRows] = useState<Row[]>([]);
	const [surgeonKeys, setSurgeonKeys] = useState<string[]>([]);

	const startISO = useAppSelector(selectReportStartDate);
	const endISO = useAppSelector(selectReportEndDate);
	const start = toYMD(startISO);
	const end = toYMD(endISO);

	// 1) Fetch data — backend already enforces "first speciality only"
	useEffect(() => {
		const run = async () => {
			let s = start,
				e = end;
			if (s && e && s > e) [s, e] = [e, s];

			console.debug(
				"[REPORTS:ECHART] fetch → /reports/specialty-stacks",
				{ start: s, end: e }
			);
			try {
				setLoading(true);
				setError(null);

				const res = await globalAxios.get(
					`${SERVER_URL}/reports/specialty-stacks`,
					{
						params: { start: s, end: e },
					}
				);

				const r: Row[] = res?.data?.rows ?? [];
				const keys: string[] = res?.data?.surgeonKeys ?? [];
				console.debug("[REPORTS:ECHART] fetch ok", {
					rows: r.length,
					surgeonKeys: keys.length,
				});

				setRows(r);
				setSurgeonKeys(keys);

				// DEBUG: quick sanity check — a surgeon should contribute to at most one speciality
				const breaches: string[] = [];
				for (const k of keys) {
					let nonZero = 0;
					for (const row of r) {
						const v = Number((row as any)[k] ?? 0);
						if (v > 0) nonZero++;
						if (nonZero > 1) {
							breaches.push(k);
							break;
						}
					}
				}
				if (breaches.length) {
					console.warn(
						"[REPORTS:ECHART] Sanity check: surgeons in multiple specialities:",
						breaches
					);
				} else {
					console.debug(
						"[REPORTS:ECHART] Sanity check passed: each surgeon appears in ≤ 1 speciality"
					);
				}
			} catch (e: any) {
				console.error("[REPORTS:ECHART] fetch failed", e);
				setError(
					e?.response?.data?.message || "Failed to load report data."
				);
			} finally {
				setLoading(false);
			}
		};
		run();
	}, [start, end]);

	// 2) Resize handling — window + ResizeObserver on container
	useEffect(() => {
		const el = hostRef.current;

		const resizeChart = () => {
			try {
				chartRef.current?.getEchartsInstance().resize();
				console.debug("[REPORTS:ECHART] resize");
			} catch {}
		};

		window.addEventListener("resize", resizeChart);

		if (!el) {
			return () => window.removeEventListener("resize", resizeChart);
		}

		let rafId = 0;
		const ro = new ResizeObserver(() => {
			cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(resizeChart);
		});

		ro.observe(el);
		resizeChart(); // initial layout

		return () => {
			window.removeEventListener("resize", resizeChart);
			cancelAnimationFrame(rafId);
			ro.disconnect();
		};
	}, []);

	// 3) Build chart option
	const option = useMemo(() => {
		console.debug("[REPORTS:ECHART] build option start");

		// compute totals & sort rows (desc), but push Uncategorised to the bottom
		const withTotals = rows.map((r) => {
			const total =
				typeof r.total === "number"
					? (r.total as number)
					: surgeonKeys.reduce(
							(sum, k) =>
								sum +
								(typeof r[k] === "number"
									? (r[k] as number)
									: 0),
							0
					  );
			return { ...r, __total: total };
		});

		const sortedRows = withTotals.sort((a, b) => {
			const aUn = normaliseSpec(a.speciality) === UNCATEGORISED;
			const bUn = normaliseSpec(b.speciality) === UNCATEGORISED;
			if (aUn !== bUn) return aUn ? 1 : -1; // Uncategorised last
			return (b as any).__total - (a as any).__total;
		});

		const categories = sortedRows.map((r) => normaliseSpec(r.speciality));
		console.debug("[REPORTS:ECHART] categories (top→bottom):", categories);

		// globally sort surgeons so stacks read left→right by overall contribution
		const surgeonTotals: Record<string, number> = {};
		surgeonKeys.forEach((k) => {
			surgeonTotals[k] = sortedRows.reduce(
				(sum, r) =>
					sum + (typeof r[k] === "number" ? (r[k] as number) : 0),
				0
			);
		});
		const sortedSurgeons = [...surgeonKeys].sort(
			(a, b) => surgeonTotals[b] - surgeonTotals[a]
		);
		console.debug(
			"[REPORTS:ECHART] surgeon order (left→right):",
			sortedSurgeons
		);

		// Abbreviation map for compact labels above segments
		const abbrMap: Record<string, string> = Object.fromEntries(
			sortedSurgeons.map((n) => [n, makeAbbr(n)])
		);

		// Label visibility threshold per row
		const MIN_RATIO = 0.06;

		const series = sortedSurgeons.map((name, idx) => ({
			name,
			type: "bar" as const,
			stack: "total",
			barWidth: 18,
			emphasis: { focus: "series" as const },

			data: sortedRows.map((r) => {
				const v = typeof r[name] === "number" ? (r[name] as number) : 0;
				return { value: v, rowTotal: (r as any).__total };
			}),

			label: {
				show: true,
				formatter: (p: any) => {
					const v = Number(p.value) || 0;
					const total = Number(p?.data?.rowTotal) || 0;
					if (!v || !total || v / total < MIN_RATIO) return "";
					return abbrMap[name];
				},
				backgroundColor: "rgba(255,255,255,0.9)",
				padding: [2, 4],
				borderRadius: 3,
				fontSize: 11,
				hideOverlap: true,
				position: "top",
			},

			labelLayout: (params: any) => {
				const rect = params?.rect;
				const cs = params?.coordSys;
				if (!rect || !cs) return {};
				const cx = rect.x + rect.width / 2;
				const estLabelW = params?.labelRect?.width ?? 24;
				const leftBound = cs.x + estLabelW / 2;
				const rightBound = cs.x + cs.width - estLabelW / 2;
				const xClamped = Math.min(rightBound, Math.max(leftBound, cx));
				return {
					x: xClamped,
					y: rect.y - 8,
					align: "center",
					verticalAlign: "bottom",
					moveOverlap: "shiftY",
				};
			},

			z: idx,
		}));

		// ---- Responsive layout bits ----
		// Longest label → compute a left margin that’s sensible
		const longest = categories.reduce((m, s) => Math.max(m, s.length), 0);
		const baseLeft = Math.round(longest * 8); // approx px per char
		const leftGrid = Math.min(320, Math.max(140, baseLeft)); // clamp

		// Dynamic height based on number of rows (keeps bars readable)
		const rowHeight = 28;
		const headerReserve = 120; // legend/toolbox/title space
		const chartHeight = Math.max(
			360,
			Math.min(900, headerReserve + categories.length * rowHeight)
		);

		const manySpecialities = categories.length > 12;
		const TOP_N = 12;
		const legendSelected = Object.fromEntries(
			sortedSurgeons.map((k, i) => [k, i < TOP_N])
		);

		const opt = {
			animation: true,
			grid: {
				left: leftGrid,
				right: 24,
				top: 84,
				bottom: 64, // room for x-axis name
				containLabel: true,
			},
			tooltip: {
				trigger: "axis",
				axisPointer: { type: "shadow" },
				formatter: (params: any[]) => {
					if (!Array.isArray(params) || params.length === 0)
						return "";
					const head = `<strong>${params[0].axisValueLabel}</strong>`;
					let total = 0;
					const lines = params
						.map((p) => {
							const v = Number(p.value) || 0;
							if (v <= 0) return null;
							total += v;
							return `${p.marker} ${p.seriesName}: ${v}`;
						})
						.filter(Boolean)
						.join("<br/>");
					return [
						head,
						lines,
						`<span style="font-weight:600">Total: ${total}</span>`,
					].join("<br/>");
				},
			},
			legend: {
				type: "scroll",
				top: 8,
				left: "center",
				data: sortedSurgeons,
				selected: legendSelected,
			},
			toolbox: {
				right: 8,
				bottom: 8, // bottom-right placement
				orient: "horizontal",
				feature: {
					saveAsImage: {
						pixelRatio: 3,
						name: `events_by_speciality_${new Date()
							.toISOString()
							.slice(0, 10)}`,
					},
					dataView,
				},
			},
			xAxis: {
				type: "value",
				name: "Events",
				nameLocation: "middle",
				nameGap: 36,
				axisLabel: { margin: 10 },
				splitLine: { show: true },
			},
			yAxis: {
				type: "category",
				data: categories,
				inverse: true, // largest at the TOP
				name: "Speciality",
				nameLocation: "start",
				nameGap: 24,
				axisLabel: { interval: 0 },
			},
			dataZoom: manySpecialities
				? [{ type: "slider", yAxisIndex: 0, right: 0, width: 12 }]
				: [],
			series,
			// store desired computed height so we can read it when rendering
			__chartHeight: chartHeight,
		} as any;

		console.debug("[REPORTS:ECHART] build option done", {
			categories: categories.length,
			series: series.length,
			leftGrid,
			chartHeight: (opt as any).__chartHeight,
		});
		return opt;
	}, [rows, surgeonKeys]);

	// 4) Render
	if (loading) {
		return (
			<Card sx={{ height: 520 }}>
				<CardHeader title="Events by Speciality" subheader="Loading…" />
				<CardContent sx={{ height: 460, p: 0, display: "flex" }}>
					<Box sx={{ m: "auto" }}>
						<CircularProgress />
					</Box>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		return (
			<Card sx={{ height: 520 }}>
				<CardHeader title="Events by Speciality" />
				<CardContent sx={{ height: 460, p: 0, display: "flex" }}>
					<Box sx={{ m: "auto" }}>
						<Typography variant="body2" color="error">
							{error}
						</Typography>
					</Box>
				</CardContent>
			</Card>
		);
	}

	if (!rows.length || !surgeonKeys.length) {
		return (
			<Card sx={{ height: 360 }}>
				<CardHeader title="Events by Speciality" />
				<CardContent sx={{ height: 300, p: 0, display: "flex" }}>
					<Box sx={{ m: "auto" }}>
						<Typography variant="body2" color="text.secondary">
							No data to display.
						</Typography>
					</Box>
				</CardContent>
			</Card>
		);
	}

	// Pull the computed height from option (see above)
	const computedHeight: number = (option as any).__chartHeight || 520;

	return (
		<Card ref={hostRef} sx={{ width: "100%" }}>
			<CardHeader
				title="Events by Speciality"
				subheader="Stacked by surgeon • X: number of events • Y: speciality"
			/>
			<CardContent sx={{ p: 0 }}>
				<ReactECharts
					ref={chartRef}
					option={option}
					notMerge={true}
					lazyUpdate={true}
					style={{ width: "100%", height: `${computedHeight}px` }}
					onChartReady={(inst) => {
						inst.resize(); // ensure initial fit
						console.debug("[REPORTS:ECHART] ready → resize");
					}}
					onEvents={{
						legendselectchanged: () => {
							setTimeout(
								() =>
									chartRef.current
										?.getEchartsInstance()
										.resize(),
								0
							);
						},
						datazoom: () => {
							setTimeout(
								() =>
									chartRef.current
										?.getEchartsInstance()
										.resize(),
								0
							);
						},
					}}
				/>
			</CardContent>
		</Card>
	);
};

export default SpecialityChart;

const dataView = {
	readOnly: true,
	optionToContent: (opt: any) => {
		try {
			const yCats: string[] = opt?.yAxis?.[0]?.data ?? [];
			const series: any[] = Array.isArray(opt?.series) ? opt.series : [];
			const surgeons: string[] = series.map((s) => s.name);

			const makeAbbr = (name: string) =>
				name
					.split(/\s+/)
					.map((w: string) => w[0]?.toUpperCase() ?? "")
					.join("")
					.slice(0, 4);
			const abbrMap: Record<string, string> = Object.fromEntries(
				surgeons.map((n) => [n, makeAbbr(n)])
			);

			const tableRows = yCats.map((cat, rowIdx) => {
				const cells = series.map((s) => {
					const d = s.data?.[rowIdx];
					const v =
						d == null
							? 0
							: typeof d === "object"
							? Number(d.value ?? 0)
							: Number(d);
					return Number.isFinite(v) ? v : 0;
				});
				const total = cells.reduce((a, b) => a + b, 0);
				return { cat, cells, total };
			});

			const fmt = (n: number) =>
				(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 });

			const css = `
        <style>
          .dvWrap { max-height: 60vh; overflow: auto; padding: 8px; }
          table.dvTable { width: 100%; border-collapse: collapse; table-layout: fixed; font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; font-size: 12px; }
          .dvTable th, .dvTable td { border: 1px solid #e0e0e0; padding: 6px 8px; }
          .dvTable th { background: #fafafa; position: sticky; top: 0; z-index: 1; }
          .dvTable th.num, .dvTable td.num { text-align: right; font-variant-numeric: tabular-nums; }
          .dvTable td.cat { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .dvTable td.total, .dvTable th.total { font-weight: 600; }
        </style>
      `;

			const colgroup =
				`<colgroup>` +
				`<col style="width: 26rem;">` + // Speciality
				surgeons.map(() => `<col style="width: 7rem;">`).join("") + // each surgeon
				`<col style="width: 8rem;">` + // Total
				`</colgroup>`;

			const thead =
				`<thead><tr>` +
				`<th class="cat">Speciality</th>` +
				surgeons
					.map(
						(s) =>
							`<th class="num" title="${s}">${
								abbrMap[s] || s
							}</th>`
					)
					.join("") +
				`<th class="num total">Total</th>` +
				`</tr></thead>`;

			const tbody =
				`<tbody>` +
				tableRows
					.map((r) => {
						const cells = r.cells
							.map((v) => `<td class="num">${fmt(v)}</td>`)
							.join("");
						return (
							`<tr>` +
							`<td class="cat" title="${r.cat}">${r.cat}</td>` +
							cells +
							`<td class="num total">${fmt(r.total)}</td>` +
							`</tr>`
						);
					})
					.join("") +
				`</tbody>`;

			return `
        ${css}
        <div class="dvWrap">
          <table class="dvTable">
            ${colgroup}
            ${thead}
            ${tbody}
          </table>
        </div>
      `;
		} catch (e) {
			console.error("[REPORTS:ECHART][DataView] build failed:", e);
			return "<div style='padding:12px;color:#d32f2f'>Failed to build data view.</div>";
		}
	},
};
